import React, { FC } from 'react';
import { shallowEqual } from 'react-redux';
import { domPropsSelector } from './ResponsiveTable';
import { DefaultCellRenderer } from './renderers/DefaultCellRenderer';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import { useDomProps } from './store/selection/useDomProps';
import { useResponsiveTable } from './store';
import { getFocusSelector } from '@apia/util';

interface ICell {
  columnIndex: number;
  inTableColumnIndex: number;
  rowIndex: number;
}

const NoMemoCell: FC<ICell> = ({
  columnIndex,
  inTableColumnIndex,
  rowIndex,
}) => {
  const { name } = useResponsiveTableContext();
  const domProps = useDomProps(
    'td',
    { columnIndex: inTableColumnIndex, rowIndex },
    domPropsSelector(name),
  );

  const cell = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];

    return tableState?.rows[rowIndex]?.cells[columnIndex];
  }, shallowEqual);

  const column = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.columns[columnIndex];
  }, shallowEqual);

  const row = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.rows[rowIndex];
  }, shallowEqual);

  const Renderer = React.useMemo(
    () => cell?.Renderer ?? column.Renderer ?? DefaultCellRenderer,
    [cell?.Renderer, column.Renderer],
  );

  if (!Renderer || !cell)
    return row.separator ? <div className="separator"></div> : null;

  return (
    <Renderer
      {...(domProps as Omit<React.HTMLProps<HTMLTableCellElement>, 'ref'>)}
      cell={cell}
      column={column}
      row={row}
      onFocus={(ev) => {
        if (ev.target instanceof HTMLElement) {
          const cell = ev.target.closest<HTMLElement>('td,th');
          if (ev.target === cell) {
            const widget = cell?.querySelector<HTMLElement>(getFocusSelector());
            if (cell && widget) {
              widget.focus();
              cell.tabIndex = -1;
            }
          }
        }
      }}
    />
  );
};

export const Cell = React.memo(NoMemoCell);
